import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Users, Award, Heart, CheckCircle2, Zap, Clock, Shield, TrendingUp } from 'lucide-react';

const values = [
  { icon: Target, title: 'Transparency', description: 'No hidden fees, clear terms, and honest communication throughout your funding journey.' },
  { icon: Users, title: 'Partnership', description: 'We see ourselves as partners in your success, not just a funding provider.' },
  { icon: Award, title: 'Excellence', description: 'We strive to deliver the best service experience in the business finance industry.' },
  { icon: Heart, title: 'Empathy', description: 'We understand the challenges businesses face and work hard to support your growth.' }
];

const journeySteps = [
  { icon: Zap, title: 'Apply Online', description: 'Complete our 2-minute application with your business details and funding needs.', time: '2 min' },
  { icon: Clock, title: 'Advisor Review', description: 'A dedicated advisor reviews your application and contacts you within 4 hours.', time: '4 hrs' },
  { icon: Shield, title: 'Documents & Verification', description: 'Upload required documents via your secure client portal for verification.', time: '1-2 days' },
  { icon: TrendingUp, title: 'Funded', description: 'Receive your tailored offer, sign digitally, and get funds transferred.', time: '24-48 hrs' }
];

// Animated counter hook
function useAnimatedCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start || target === 0) return;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration, start]);
  return count;
}

// Intersection Observer hook
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedStat({ value, suffix = '', label }) {
  const [ref, inView] = useInView();
  const count = useAnimatedCounter(value, 2200, inView);
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl sm:text-5xl font-bold text-white font-['Outfit'] tabular-nums">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-blue-300 mt-2 font-medium">{label}</div>
    </div>
  );
}

export default function AboutPage() {
  const navigate = useNavigate();
  const [journeyRef, journeyInView] = useInView(0.1);

  const handleApplyClick = () => {
    navigate('/#apply');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <Header />

      {/* Hero */}
      <section className="py-24 sm:py-32 bg-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">About Us</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight leading-tight mb-6 font-['Outfit']">
                Empowering UK Businesses to Thrive
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                QuickFinance was founded with a simple mission: to make business funding accessible, fast, and fair. We believe every business deserves the opportunity to grow, and traditional lending barriers shouldn't stand in the way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleApplyClick} className="bg-blue-700 hover:bg-blue-800 text-white btn-primary" data-testid="about-apply-btn">
                  Apply Now <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => navigate('/contact')} className="border-slate-300" data-testid="about-contact-btn">
                  Get in Touch
                </Button>
              </div>
            </div>
            <div className="relative grid grid-cols-2 gap-4">
              {[
                { icon: Shield, title: 'FCA Regulated', desc: 'Fully regulated credit broker', color: 'bg-blue-600' },
                { icon: Users, title: '30+ Lenders', desc: 'Access our full lender panel', color: 'bg-emerald-600' },
                { icon: Clock, title: '4hr Decisions', desc: 'Rapid eligibility checks', color: 'bg-violet-600' },
                { icon: Award, title: '98% Satisfaction', desc: 'Rated excellent by clients', color: 'bg-amber-600' }
              ].map((item, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3">
                  <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center`}>
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-sm font-semibold text-slate-900 font-['Outfit']">{item.title}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Animated Stats Banner */}
      <section className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 py-20" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight font-['Outfit']">
              Our Impact in Numbers
            </h2>
            <p className="text-blue-300 mt-3 max-w-xl mx-auto">
              Helping businesses across the UK unlock their growth potential.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat value={500} suffix="M+" label="Funding Facilitated" />
            <AnimatedStat value={4500} suffix="+" label="Businesses Funded" />
            <AnimatedStat value={98} suffix="%" label="Client Satisfaction" />
            <AnimatedStat value={4} suffix=" hrs" label="Avg. Response Time" />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6 font-['Outfit']">
            Our Mission
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            "To provide UK businesses with fast, flexible, and transparent funding solutions that empower growth and success. We're committed to making business finance simple and accessible for everyone."
          </p>
        </div>
      </section>

      {/* Animated Journey - "How We Fund Your Business" */}
      <section className="py-24 sm:py-32 bg-white overflow-hidden" data-testid="journey-section" ref={journeyRef}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight font-['Outfit']">
              How We Fund Your Business
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              From application to funding in as little as 48 hours. Here's what the journey looks like.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {journeySteps.map((step, idx) => (
              <div
                key={idx}
                className="relative"
                style={{
                  opacity: journeyInView ? 1 : 0,
                  transform: journeyInView ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.5s ${idx * 0.2}s, transform 0.5s ${idx * 0.2}s`
                }}
                data-testid={`journey-step-${idx}`}
              >
                {/* Arrow connector (desktop only, between cards) */}
                {idx < journeySteps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={journeyInView ? 1 : 0} style={{ transition: `opacity 0.4s ${0.8 + idx * 0.2}s` }} />
                    </svg>
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 h-full flex flex-col items-center text-center hover:border-blue-200 hover:shadow-md transition-all">
                  {/* Step number */}
                  <div className="w-8 h-8 bg-slate-900 text-white rounded-full text-sm font-bold flex items-center justify-center mb-4">
                    {idx + 1}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-700 ${
                      journeyInView ? 'bg-blue-700 shadow-lg shadow-blue-700/20' : 'bg-slate-200'
                    }`}
                    style={{ transitionDelay: `${idx * 0.2}s` }}
                  >
                    <step.icon className="w-6 h-6 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-base font-semibold text-slate-900 font-['Outfit'] mb-2">{step.title}</h3>

                  {/* Description - fixed height for alignment */}
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 mb-4">{step.description}</p>

                  {/* Time badge */}
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
                    <Clock className="w-3 h-3" /> {step.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight font-['Outfit']">
              What We Stand For
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 text-center card-hover" data-testid={`value-card-${i}`}>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Outfit']">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 sm:py-32 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Why QuickFinance</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6 font-['Outfit']">
                A Different Kind of Broker
              </h2>
              <p className="text-slate-600 mb-8">
                Unlike traditional banks, we focus on your business potential, not just your credit history. Our streamlined process and dedicated team ensure you get the funding you need without the hassle.
              </p>
              <ul className="space-y-4">
                {[
                  'FCA regulated for your peace of mind',
                  'No hidden fees or unexpected charges',
                  'Transparent pricing from day one',
                  'UK-based support team available 6 days a week',
                  'Dedicated account manager for every client'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900 rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6 font-['Outfit']">Ready to Get Started?</h3>
              <p className="text-slate-300 mb-8">
                Apply today and discover why thousands of UK businesses trust QuickFinance for their funding needs.
              </p>
              <Button onClick={handleApplyClick} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12" data-testid="about-cta-btn">
                Start Your Application <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
